import type { InputFrame } from "../shared/types";

/** Standard Gamepad mapping (Xbox / Switch Pro in browser). */
export const GAMEPAD = {
  A: 0,
  B: 1,
  X: 2,
  Y: 3,
  SELECT: 8,
  START: 9,
  DPAD_UP: 12,
  DPAD_DOWN: 13,
  DPAD_LEFT: 14,
  DPAD_RIGHT: 15
} as const;

const STICK_DEADZONE = 0.3;

export function connectedGamepad(
  pads: Array<Gamepad | null | undefined> = [...(navigator.getGamepads?.() ?? [])]
): Gamepad | null {
  return pads.find((candidate) => candidate?.connected) ?? null;
}

export function gamepadButtonDown(pad: Gamepad | null | undefined, index: number): boolean {
  return Boolean(pad?.buttons[index]?.pressed);
}

export function gamepadStickAxis(pad: Gamepad | null | undefined, axisIndex: number): -1 | 0 | 1 {
  const value = pad?.axes[axisIndex] ?? 0;
  if (Math.abs(value) <= STICK_DEADZONE) return 0;
  return value > 0 ? 1 : -1;
}

export function readGamepadCombat(pad: Gamepad | null | undefined): Pick<InputFrame, "attack" | "jump" | "special"> {
  return {
    attack: gamepadButtonDown(pad, GAMEPAD.Y),
    jump: gamepadButtonDown(pad, GAMEPAD.B),
    special: gamepadButtonDown(pad, GAMEPAD.X)
  };
}

export function readGamepadMove(pad: Gamepad | null | undefined): Pick<InputFrame, "x" | "y"> {
  const stickX = gamepadStickAxis(pad, 0);
  const stickY = gamepadStickAxis(pad, 1);
  return {
    x: stickX !== 0 ? stickX : dpadAxis(pad, GAMEPAD.DPAD_LEFT, GAMEPAD.DPAD_RIGHT),
    y: stickY !== 0 ? stickY : dpadAxis(pad, GAMEPAD.DPAD_UP, GAMEPAD.DPAD_DOWN)
  };
}

export function gamepadHasActivity(pad: Gamepad | null | undefined): boolean {
  if (!pad?.connected) return false;
  if (pad.buttons.some((button) => button.pressed)) return true;
  return pad.axes.some((value) => Math.abs(value) > STICK_DEADZONE);
}

function dpadAxis(pad: Gamepad | null | undefined, negative: number, positive: number): -1 | 0 | 1 {
  const left = gamepadButtonDown(pad, negative);
  const right = gamepadButtonDown(pad, positive);
  return left === right ? 0 : left ? -1 : 1;
}

export function risingEdge(wasDown: boolean, isDown: boolean): { pressed: boolean; down: boolean } {
  return { pressed: isDown && !wasDown, down: isDown };
}
